import PropTypes from 'prop-types';
import cx from 'classnames';

import sty from './DataTable.module.scss';

function renderHeader(col, { sort, onSort }) {
  const isActiveSort = sort && sort.field === col.key;
  const ascending = isActiveSort ? Boolean(sort.order) : true;

  if (!col.sortable) return col.header;

  const handleClick = () => {
    if (!onSort) return;
    onSort({ field: col.key, type: col.sortType || 'string' });
  };
  const handleKey = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <span
      className={cx(sty.sortable, isActiveSort && sty.sortActive)}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKey}
    >
      <span>{col.header}</span>
      {isActiveSort && (
        <span className={sty.sortArrow} aria-hidden="true">
          {ascending ? '▲' : '▼'}
        </span>
      )}
    </span>
  );
}

function DataTable({
  columns,
  data,
  rowKey,
  onRowClick,
  sort,
  onSort,
  emptyMessage = 'No data found.',
  className,
}) {
  const colCount = columns.length;
  const isEmpty = !data || data.length === 0;

  return (
    <div className={cx(sty.wrap, className)}>
      <table className={sty.table}>
        <colgroup>
          {columns.map(col => (
            <col key={col.key} style={col.width ? { width: col.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={cx(
                  col.align === 'center' && sty.alignCenter,
                  col.align === 'right' && sty.alignRight,
                  col.headerClassName,
                )}
              >
                {renderHeader(col, { sort, onSort })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td colSpan={colCount} className={sty.empty}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map(row => {
              const key = rowKey ? rowKey(row) : row.id;
              const clickable = Boolean(onRowClick);
              const handleRowClick = () => clickable && onRowClick(row);
              const handleRowKey = e => {
                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onRowClick(row);
                }
              };
              return (
                <tr
                  key={key}
                  className={cx(clickable && sty.clickable)}
                  onClick={clickable ? handleRowClick : undefined}
                  onKeyDown={clickable ? handleRowKey : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? 'button' : undefined}
                >
                  {columns.map(col => {
                    const content = col.render ? col.render(row) : row[col.key];
                    const stop = col.stopRowClick ? e => e.stopPropagation() : undefined;
                    return (
                      <td
                        key={col.key}
                        className={cx(
                          col.align === 'center' && sty.alignCenter,
                          col.align === 'right' && sty.alignRight,
                          col.cellClassName,
                        )}
                        onClick={stop}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

const columnShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  header: PropTypes.node,
  sortable: PropTypes.bool,
  sortType: PropTypes.oneOf(['string', 'date', 'number']),
  width: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  headerClassName: PropTypes.string,
  cellClassName: PropTypes.string,
  render: PropTypes.func,
  stopRowClick: PropTypes.bool,
});

DataTable.propTypes = {
  columns: PropTypes.arrayOf(columnShape).isRequired,
  data: PropTypes.array,
  rowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  sort: PropTypes.oneOfType([
    PropTypes.shape({
      field: PropTypes.string,
      type: PropTypes.string,
      order: PropTypes.bool,
    }),
    PropTypes.string,
  ]),
  onSort: PropTypes.func,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
};

export default DataTable;
